import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { FileCheck2, FileText, Ban, CheckCircle2, XCircle, ShieldCheck, AlertTriangle, RotateCcw, X } from "lucide-react";

const STATUS_META = {
  DRAFT: { label: "Brouillon", cls: "bg-neutral-100 text-neutral-600" },
  PENDING_VERIFICATION: { label: "À vérifier", cls: "bg-amber-50 text-amber-700" },
  PENDING_SIGNATURE: { label: "À signer", cls: "bg-blue-50 text-blue-700" },
  ACTIVE: { label: "Actif", cls: "bg-emerald-50 text-emerald-700" },
  SUSPENDED: { label: "Suspendu", cls: "bg-red-50 text-red-700" },
  TERMINATED: { label: "Résilié", cls: "bg-neutral-200 text-neutral-500" },
};

const CONTRACT_META = {
  GENERATED: "Généré",
  SENT: "Envoyé",
  VIEWED: "Consulté",
  SIGNED: "Signé",
};

const FILTERS = ["", "PENDING_VERIFICATION", "PENDING_SIGNATURE", "ACTIVE", "SUSPENDED", "TERMINATED"];

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [filter, setFilter] = useState("");
  const [banTarget, setBanTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/partners", { params: filter ? { status: filter } : {} });
      setPartners(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const unban = async (p) => {
    if (!window.confirm(`Réintégrer « ${p.company_name || p.name} » ? Ses formules suspendues par le bannissement seront réactivées.`)) return;
    try {
      await api.post(`/admin/partners/${p.id}/ban`, { banned: false, reason: "" });
      toast.success("Auto-école réintégrée.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const approve = async (p) => {
    try {
      await api.post(`/admin/partners/${p.id}/approve`);
      toast.success("Contrat généré et envoyé au partenaire pour signature.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const setStatus = async (p, status, msg) => {
    try {
      await api.post(`/admin/partners/${p.id}/status`, { status });
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const viewContract = (p) => {
    window.open(`${api.defaults.baseURL}/admin/partners/${p.id}/contract/pdf`, "_blank");
  };

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <h1 className="text-3xl font-extrabold tracking-tighter">Partenaires</h1>
        <p className="mt-2 text-neutral-500">
          Cycle de vie des auto-écoles : vérification → contrat → signature → activation. Un partenaire ne reçoit
          des prospects qu’une fois <b>actif</b> (contrat signé).
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f || "all"}
              onClick={() => setFilter(f)}
              data-testid={`partner-filter-${f || "all"}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
                filter === f ? "bg-brand text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
              }`}
            >
              {f ? STATUS_META[f].label : "Tous"}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-partners-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Partenaire</th>
                <th className="px-4 py-3">SIREN</th>
                <th className="px-4 py-3">Ville</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Manquements</th>
                <th className="px-4 py-3">Contrat</th>
                <th className="px-4 py-3">Prospects</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {partners.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-400">Aucun partenaire.</td></tr>
              ) : (
                partners.map((p) => {
                  const meta = STATUS_META[p.status] || STATUS_META.ACTIVE;
                  return (
                    <tr key={p.id} data-testid={`partner-row-${p.id}`} className={p.banned ? "bg-red-50/40" : ""}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-neutral-800">{p.company_name || p.name}</div>
                        <div className="text-xs text-neutral-400">{p.email}</div>
                        {p.banned && (
                          <div className="mt-1 flex items-start gap-1 text-xs text-red-600" data-testid={`partner-ban-reason-${p.id}`}>
                            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> Banni{p.ban_reason ? ` — ${p.ban_reason}` : ""}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{p.siren || "—"}</td>
                      <td className="px-4 py-3">{p.city || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${meta.cls}`} data-testid={`partner-status-${p.id}`}>
                            {meta.label}
                          </span>
                          {p.banned && (
                            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-red-600 px-2.5 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`partner-banned-badge-${p.id}`}>
                              <Ban className="h-3 w-3" /> Banni
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-testid={`partner-manquements-${p.id}`}>
                        {p.suspended_affiches_count > 0 ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-800" title="Formules suspendues pour non-conformité">
                            <AlertTriangle className="h-3.5 w-3.5" /> {p.suspended_affiches_count}
                          </span>
                        ) : (
                          <span className="text-xs text-neutral-300">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-500">
                        {p.contract_status ? (CONTRACT_META[p.contract_status] || p.contract_status) : "—"}
                      </td>
                      <td className="px-4 py-3">{p.leads_count} / {p.confirmed_count} conf.</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {p.status === "PENDING_VERIFICATION" && (
                            <button onClick={() => approve(p)} data-testid={`partner-approve-${p.id}`} className="inline-flex items-center gap-1 rounded-md bg-brand px-2.5 py-1.5 text-xs font-bold text-white hover:bg-brand-hover">
                              <FileCheck2 className="h-3.5 w-3.5" /> Valider & générer contrat
                            </button>
                          )}
                          {p.contract_status && (
                            <button onClick={() => viewContract(p)} title="Voir le contrat" className="rounded-md p-1.5 text-neutral-500 hover:bg-neutral-100 hover:text-brand">
                              <FileText className="h-4 w-4" />
                            </button>
                          )}
                          {p.status === "ACTIVE" && !p.banned && (
                            <button onClick={() => setStatus(p, "SUSPENDED", "Partenaire suspendu.")} title="Suspendre" data-testid={`partner-suspend-${p.id}`} className="rounded-md p-1.5 text-amber-600 hover:bg-neutral-100">
                              <XCircle className="h-4 w-4" />
                            </button>
                          )}
                          {p.status === "SUSPENDED" && !p.banned && (
                            <button onClick={() => setStatus(p, "ACTIVE", "Partenaire réactivé.")} title="Réactiver" className="rounded-md p-1.5 text-emerald-600 hover:bg-neutral-100">
                              <CheckCircle2 className="h-4 w-4" />
                            </button>
                          )}
                          {p.banned ? (
                            <button onClick={() => unban(p)} data-testid={`partner-unban-${p.id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-emerald-200 px-2.5 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50">
                              <RotateCcw className="h-3.5 w-3.5" /> Réintégrer
                            </button>
                          ) : (
                            <button onClick={() => setBanTarget(p)} data-testid={`partner-ban-${p.id}`}
                              className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">
                              <Ban className="h-3.5 w-3.5" /> Bannir
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <p className="mt-4 flex items-center gap-1.5 text-xs text-neutral-400">
          <ShieldCheck className="h-3.5 w-3.5" /> Toutes les actions (validation, signature, suspension, bannissement) sont tracées dans le journal d’audit.
        </p>
      </div>
      {banTarget && (
        <BanModal
          partner={banTarget}
          onClose={() => setBanTarget(null)}
          onDone={() => { setBanTarget(null); load(); }}
        />
      )}
    </Shell>
  );
}

function BanModal({ partner, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error("Indique le motif du bannissement (communiqué à l'auto-école)."); return; }
    setSaving(true);
    try {
      await api.post(`/admin/partners/${partner.id}/ban`, { banned: true, reason: reason.trim() });
      toast.success("Auto-école bannie — ses formules sont bloquées.");
      onDone();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const REASONS = [
    "Nom de l'auto-école mentionné dans ses formules (interdit).",
    "Contact direct de l'élève hors plateforme (désintermédiation).",
    "Non-respect répété des règles de Plan Permis.",
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="partner-ban-modal">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-red-600" />
            <h2 className="text-lg font-extrabold text-neutral-900">Bannir l'auto-école</h2>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100"><X className="h-5 w-5" /></button>
        </div>
        <p className="mt-2 text-sm text-neutral-500">
          <strong>{partner.company_name || partner.name}</strong> sera retirée du parcours élève et
          <strong> toutes ses formules seront immédiatement bloquées</strong>. L'auto-école recevra le motif par e-mail.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {REASONS.map((r) => (
            <button key={r} onClick={() => setReason(r)} type="button"
              className="rounded-full border border-neutral-200 px-3 py-1 text-xs text-neutral-600 hover:border-red-300 hover:text-red-600">
              {r}
            </button>
          ))}
        </div>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          data-testid="partner-ban-reason"
          placeholder="Motif du bannissement (obligatoire)…"
          className="mt-3 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-red-400"
        />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-bold text-neutral-700 hover:border-neutral-400">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="partner-ban-confirm"
            className="rounded-full bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60">
            {saving ? "Bannissement…" : "Bannir l'auto-école"}
          </button>
        </div>
      </div>
    </div>
  );
}
